import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import {
  ArrowUpRight,
  ArrowRight,
  Pill,
  Heart,
  Truck,
  FileText,
  MapPin,
  Phone,
  Mail,
  Plus,
  Check,
  ShoppingBag,
} from "lucide-react";
import logo from "../images/logo2.svg";
import "./LaunchingSoon.css";

const services = [
  {
    icon: Pill,
    title: "Your everyday pharmacy",
    text: "Find medicines and healthcare essentials, with brands and categories in one convenient place.",
  },
  {
    icon: Heart,
    title: "A little more well-being",
    text: "Explore personal care, nutrition and wellness products for you and your family.",
  },
  {
    icon: FileText,
    title: "Prescriptions, simplified",
    text: "Upload your prescription and let our team review it to help you with your medicine order.",
  },
  {
    icon: Truck,
    title: "Care that comes to you",
    text: "Choose home delivery or store pickup, and follow your order from your account.",
  },
];

export default function LaunchingSoon() {
  return (
    <div className="launch-page">
      <Helmet>
        <title>MediSYS Pharmacy | A healthier everyday. Launching Soon.</title>
        <meta
          name="description"
          content="A fresh MediSYS Pharmacy experience is coming. Discover medicines, wellness, prescription support, home delivery and store pickup in Hyderabad."
        />
      </Helmet>
      <a className="launch-skip" href="#launch-main">
        Skip to content
      </a>
      <header className="launch-nav launch-wrap">
        <Link to="/" aria-label="MediSYS landing page">
          <img src={logo} alt="MediSYS Pharmacy" />
        </Link>
        <nav aria-label="Main navigation">
          <a className="launch-nav-about" href="#our-story">
            Our story
          </a>
          <a className="launch-nav-about" href="#coming-your-way">
            What’s coming
          </a>
          <a href="#stay-connected" className="launch-contact">
            Get in touch <ArrowUpRight size={16} />
          </a>
        </nav>
      </header>

      <main id="launch-main">
        <section
          className="launch-hero launch-wrap"
          aria-labelledby="launch-title"
        >
          <div className="launch-hero-copy">
            <span className="launch-status">
              <span /> A fresh experience. Launching soon.
            </span>
            <p className="launch-eyebrow">HELLO, A HEALTHIER EVERYDAY.</p>
            <h1 id="launch-title">
              Good health.
              <br />
              Great care.
              <br />
              <em>Closer than ever.</em>
            </h1>
            <p className="launch-intro">
              Your neighbourhood pharmacy, with a whole new way to care. We’re
              bringing medicines, wellness and everyday essentials together in
              one thoughtful online experience.
            </p>
            <div className="launch-hero-actions">
              <a className="launch-button" href="#coming-your-way">
                Discover what’s coming <ArrowRight size={18} />
              </a>
            </div>
            <div className="launch-local">
              <MapPin size={17} />
              <span>Rooted in Hyderabad. Built around you.</span>
            </div>
          </div>

          <div
            className="launch-art"
            aria-label="MediSYS brings pharmacy, prescriptions and wellness together"
          >
            <div className="launch-orbit launch-orbit-one" />
            <div className="launch-orbit launch-orbit-two" />
            <span className="launch-spark launch-spark-one" aria-hidden="true">
              ✳
            </span>
            <span className="launch-spark launch-spark-two" aria-hidden="true">
              +
            </span>
            <div className="launch-art-label">
              A LITTLE CARE GOES A LONG WAY
            </div>
            <div className="launch-bag">
              <div className="launch-bag-handle" />
              <div className="launch-bag-front">
                <div className="launch-cross">
                  <Plus size={62} strokeWidth={3} />
                </div>
                <img src={logo} alt="" />
                <span>YOUR HEALTH. OUR PRIORITY.</span>
                <div className="launch-bag-line" />
              </div>
            </div>
            <div className="launch-float launch-float-rx">
              <span className="launch-mini-icon">
                <FileText size={22} />
              </span>
              <div>
                <strong>Less hassle.</strong>
                <small>Prescription support</small>
              </div>
              <Check size={16} />
            </div>
            <div className="launch-float launch-float-care">
              <span className="launch-mini-icon orange">
                <Heart size={22} />
              </span>
              <div>
                <strong>More well-being.</strong>
                <small>Care for the whole family</small>
              </div>
            </div>
            <div className="launch-art-note">
              <ShoppingBag size={15} /> Your new wellness destination
            </div>
          </div>
        </section>

        <div className="launch-ribbon">
          <div className="launch-wrap">
            <span>MEDICINES</span>
            <Plus />
            <span>WELLNESS</span>
            <Plus />
            <span>PERSONAL CARE</span>
            <Plus />
            <span>EVERYDAY ESSENTIALS</span>
          </div>
        </div>

        <section id="our-story" className="launch-story launch-wrap">
          <div>
            <p className="launch-eyebrow">THE MEDISYS WAY</p>
            <h2>
              More than a pharmacy.
              <br />
              <em>A part of your everyday.</em>
            </h2>
          </div>
          <div>
            <p>
              Founded in 2022 in Hyderabad, MediSYS Pharmacy brings healthcare,
              wellness and lifestyle essentials together through our Pharma Mall
              concept.
            </p>
            <p>
              Now, we’re bringing that same care online. From discovering the
              right essentials to managing prescriptions and orders, we’re
              making your next step towards well-being a little simpler.
            </p>
            {/* <Link className="launch-text-link" to="/about-us">
              Meet MediSYS <ArrowUpRight size={17} />
            </Link> */}
          </div>
        </section>

        <section id="coming-your-way" className="launch-services launch-wrap">
          <div className="launch-section-heading">
            <div>
              <p className="launch-eyebrow">THOUGHTFULLY CONNECTED</p>
              <h2>
                Everything for your
                <br />
                <em>wellness journey.</em>
              </h2>
            </div>
            <p>
              A familiar pharmacy.
              <br />A fresh digital experience.
            </p>
          </div>
          <div className="launch-service-grid">
            {services.map(({ icon: Icon, title, text }, index) => (
              <article className="launch-service" key={title}>
                <div className="launch-service-top">
                  <Icon size={27} strokeWidth={1.5} />
                  <span>0{index + 1}</span>
                </div>
                <h3>{title}</h3>
                <p>{text}</p>
              </article>
            ))}
          </div>
        </section>

        <section id="stay-connected" className="launch-connect launch-wrap">
          <div className="launch-connect-inner">
            <div>
              <span className="launch-status">
                <span /> THE NEXT CHAPTER IS ALMOST HERE
              </span>
              <h2>
                A new experience.
                <br />
                <em>The same care for you.</em>
              </h2>
              <p>
                Our new online experience is launching soon. In the meantime,
                explore MediSYS or connect with our team.
              </p>
            </div>
            <div className="launch-contact-list">
              <a href="tel:+917799923501">
                <Phone size={21} />
                <span>
                  <small>LET’S TALK</small>+91 7799923501
                </span>
                <ArrowUpRight size={18} />
              </a>
              <a href="mailto:medisys.customercare@gmail.com">
                <Mail size={21} />
                <span>
                  <small>WRITE TO US</small>medisys.customercare@gmail.com
                </span>
                <ArrowUpRight size={18} />
              </a>
              <Link>
                <MapPin size={21} />
                <span>
                  <small>FIND US NEAR YOU</small>Our stores in Hyderabad
                </span>
                <ArrowUpRight size={18} />
              </Link>
            </div>
          </div>
        </section>
      </main>

      <footer className="launch-footer launch-wrap">
        <div>
          <img src={logo} alt="MediSYS Pharmacy" />
          <p>Healthcare. Convenience. A little more care.</p>
        </div>
        <div className="launch-footer-links">
          <Link to="/launch/terms-and-conditions">Terms &amp; Conditions</Link>
          <Link to="/launch/privacy-policy">Privacy Policy</Link>
          <a
            href="https://novagito.com"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "#16a34a" }}
          >
            Built by Novagito AI
          </a>
        </div>
        <p className="launch-copyright">
          © {new Date().getFullYear()} MediSYS Pharmacy. All rights reserved.
        </p>
      </footer>
    </div>
  );
}
